import React, { useEffect, useRef, useState } from 'react';
import { FlexRow, TableDataState, toastSuccess, usePaginatedQueryV8 } from 'motion-components';
import { Button, Card } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { gqlGetQuery } from '../graphql';
import { buildWhereFromFilterValues, Filters } from './filters';
import { DataGrid } from './grid';
import { handleExport } from './export-columns';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { observer } from 'mobx-react-lite';
import { FaTimes, FaShoppingCart } from 'react-icons/fa';
import { promiseModalFinalDestInput } from './modal-finaldest-promise';
import { convertFieldToFloat } from 'shared-pages/common/form-helpfunc';

export const gqlAdd = gql`
  mutation addIdocFinalDestinationTranaction($data: IdocFinalDestinationTranaction) {
    addIdocFinalDestinationTranaction(data: $data)
  }
`;

/* ----------------------------------------------------------- */
/* ------------------------ Page ----------------------------- */
export const Idocs4FinaldestGridPage = observer(
  ({
    collectionPointId,
    isGridInForm = false,
  }: {
    collectionPointId?: number;
    isGridInForm?: boolean;
  }) => {
    const { appStore, tableStore } = useRootStore();
    const { handleGraphqlError } = useHandleError('IdocsGridPage');

    const location = useLocation<any>();
    const history = useHistory();
    const [selected, setSelected] = useState<ReadonlyArray<any>>([]);
    const { t } = useTranslation();

    const [addNewRecord] = useMutation(gqlAdd);

    const updateSelected = (rec, isSelected) => {
      const idFieldName = 'id';
      const nameFieldName = 'identificationDocument';
      const extraFieldName = 'netWeight';

      if (isSelected) {
        const newPair = {
          [idFieldName]: rec[idFieldName],
          [nameFieldName]: rec[nameFieldName],
          [extraFieldName]: rec[extraFieldName],
        };

        setSelected((prev) => [...prev, newPair]);
      } else {
        // Αφαίρεση από τη γραμμή επιλεγμένων εντύπων
        setSelected((prev) => prev.filter((item) => item[idFieldName] !== rec[idFieldName]));
        // Αποεπιλογή checkbox στο grid
        if (gridRef.current) {
          const table = (gridRef.current as any).table;
          const deselectRowById = (rowId: string) => {
            const row = table.getRow(rowId);
            if (row) {
              row.toggleSelected(false);
            }
          };
          const rowData = data.find((v) => v[idFieldName] === rec[idFieldName]);
          if (rowData) {
            deselectRowById(rec[idFieldName]);
          }
        }
      }
    };

    const { canView, canAddNew, canEdit, canDelete } = useUserLocationPermissions();

    /** Καταχώρηση τελικής διάθεσης για τα επιλεγμένα έντυπα */
    const handleFinDestDelivery = async (idocs) => {
      const finalDestinationNetWeight = idocs.reduce((sum, item) => {
        return sum + item.netWeight;
      }, 0);

      const values = await promiseModalFinalDestInput({ finalDestinationNetWeight });
      if (values === false) return;
      convertFieldToFloat(values, ['finalDestinationNetWeight']);
      const idocIds = idocs.map((values) => {
        return { id: values.id };
      });
      addNewRecord({ variables: { data: { ...values, idocIds } } })
        .then((d) => {
          toastSuccess(
            `Επιτυχημένη Προσθήκη Εγγραφής [${d?.data?.addIdocFinalDestinationTranaction}]`,
          );
          setSelected([]);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
        });
    };

    /* ------------------- Query ------------------------------- */
    const initOrderBy = 'id DESC';
    const query = usePaginatedQueryV8(
      gqlGetQuery,
      buildWhereFromFilterValues,
      location.state?.order ?? initOrderBy,
      appStore,
      true,
      { isActive: true, collectionPointId: collectionPointId, finalDestinationDate: null },
      false, // Χρειάζεται false για να λειτουργεί το multiSelect
      !isGridInForm ? undefined : 'idocs_grid',
    );
    const {
      loading,
      data,
      dataCount,
      error,
      initPageIndex,
      filtersChanged,
      gridPageChanged,
      queryState,
      queryParams,
      setQueryParams,
      refetch,
    } = query;

    const client = useApolloClient();

    const gridRef = useRef();
    useEffect(() => {
      console.log('IdocData', data, 'selected', selected);
    }, [data]);

    if (error) {
      handleGraphqlError('query-get', error);
      return <h3 className="text-center">{error.message}</h3>;
    } else {
      return (
        <Card className="shadow">
          {!isGridInForm && (
            <Card.Header>
              <FlexRow>
                <Card.Title className="m-0">
                  {t('Έντυπα Αναγνώρισης Αποβλήτων προς Τελική Διάθεση')}
                </Card.Title>
              </FlexRow>
            </Card.Header>
          )}

          <Card.Body className="nopadding">
            {/* Φίλτρα */}
            <Filters
              onChange={filtersChanged}
              initialValues={queryState?.filterValues ?? {}}
              disable={tableStore.state !== TableDataState.browse}
              isGridInForm={isGridInForm}
            />

            {/* Επιλεγμένα έντυπα */}
            {selected && (
              <div className="mt-2 mb-0">
                <SelectedItems
                  selected={selected}
                  updateSelected={updateSelected}
                  handleFinDestDelivery={handleFinDestDelivery}
                />
              </div>
            )}

            {/* Grid */}
            <DataGrid
              ref={gridRef}
              loading={loading}
              data={data}
              dataCount={dataCount}
              initPageIndex={initPageIndex}
              onPageChange={gridPageChanged}
              handleExport={() => {
                handleExport(client, queryState, t);
              }}
              initOrderBy={queryParams?.order ?? initOrderBy}
              onOrderChange={(orderBy) => {
                const newQueryParams = { ...queryParams, order: orderBy };
                setQueryParams(newQueryParams);
                history.replace({
                  pathname: location.pathname,
                  state: {
                    ...location.state,
                    order: orderBy,
                  },
                });
              }}
              selected={selected}
              setSelected={setSelected}
              updateSelected={updateSelected}
              refetch={refetch}
              isGridInForm={isGridInForm}
            />
          </Card.Body>
        </Card>
      );
    }
  },
);

const SelectedItems = ({ selected, updateSelected, handleFinDestDelivery }) => {
  const nameFieldName = 'identificationDocument';
  return (
    <div>
      {selected && selected.length > 0 && (
        <Button variant="info" className="me-2" onClick={() => handleFinDestDelivery(selected)}>
          <FaShoppingCart className="pb-1" />
          &nbsp; Θέσε Τελική Διάθεση στα Έντυπα:
        </Button>
      )}
      {selected.map((item, index) => (
        <Button key={index} variant="warning" className="me-2 mb-2 mt-2 ">
          {item[nameFieldName]}{' '}
          <FaTimes className="pb-1" onClick={() => updateSelected(item, false)} />
        </Button>
      ))}
    </div>
  );
};
